import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Eye, EyeOff, ArrowLeft, BookMarked, Check, Sliders } from "lucide-react";
import api from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { AppHeader } from "@/components/AppHeader";

const empty = {
  title: "",
  description: "",
  checkbox_label: "Ich stimme zu",
  order_index: 100,
  enabled: true,
  requires_check_to_sign: false,
};

export default function AdminSigningOptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  // Iter88zaq (2026-02-18) — Vorlagen-Bibliothek
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [tplBusyKey, setTplBusyKey] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/admin/signing-options");
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };
  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/admin/signing-options/templates");
      setTemplates(data || []);
    } catch (e) {
      // Silent — Templates sind optional; falls Endpoint fehlt zeigen wir nur den Add-Button
    }
  };
  useEffect(() => { load(); loadTemplates(); }, []);
  // Iter88zbu — Silent-Reload nach jeder Mutation.
  useReloadBus("*", () => load(true));

  const save = async () => {
    try {
      if (!edit.title?.trim()) return toast.error("Titel darf nicht leer sein");
      if (edit.id) {
        await api.patch(`/admin/signing-options/${edit.id}`, edit);
        toast.success("Aktualisiert");
      } else {
        await api.post("/admin/signing-options", edit);
        toast.success("Erstellt");
      }
      setEdit(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Diese Option wirklich löschen?")) return;
    try {
      await api.delete(`/admin/signing-options/${id}`);
      toast.success("Gelöscht");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const toggleEnabled = async (o) => {
    try {
      await api.patch(`/admin/signing-options/${o.id}`, { enabled: !o.enabled });
      await load();
    } catch (e) {
      toast.error("Fehler beim Umschalten");
    }
  };

  // Iter88zaq — Vorlage per Klick als neue Signatur-Option anlegen.
  // Direktes POST spart einen zusätzlichen Modal-Klick, entspricht dem
  // Wunsch „per Klick übernehmen".
  const applyTemplate = async (tpl) => {
    if (tpl.already_used) {
      toast.info("Diese Vorlage ist bereits als Option vorhanden.");
      return;
    }
    setTplBusyKey(tpl.key);
    try {
      const { title, description, checkbox_label, order_index, enabled, requires_check_to_sign } = tpl;
      await api.post("/admin/signing-options", { title, description, checkbox_label, order_index, enabled, requires_check_to_sign });
      toast.success(`Vorlage „${tpl.title}" übernommen`);
      await Promise.all([load(), loadTemplates()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorlage konnte nicht übernommen werden");
    } finally {
      setTplBusyKey(null);
    }
  };

  // Iter88zar (2026-02-18) — „Vorlage anpassen"-Modus: Öffnet den
  // Edit-Modal mit vorbefüllten Feldern (ohne `id`, damit beim Speichern
  // POST statt PATCH ausgeführt wird). Admin kann Titel/Text/Reihenfolge
  // vor dem Speichern individuell tweaken.
  const customizeTemplate = (tpl) => {
    if (tpl.already_used) {
      toast.info("Diese Vorlage ist bereits als Option vorhanden.");
      return;
    }
    setEdit({
      title: tpl.title,
      description: tpl.description,
      checkbox_label: tpl.checkbox_label,
      order_index: tpl.order_index,
      enabled: tpl.enabled,
      requires_check_to_sign: tpl.requires_check_to_sign,
      _from_template: tpl.key,  // rein für Anzeige-Hinweis im Modal
    });
    setShowTemplates(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <AppHeader />
      <div className="max-w-5xl mx-auto p-4 sm:p-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-[#00E575] mb-3 transition">
          <ArrowLeft size={14} /> Zurück zum Dashboard
        </Link>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Signatur-Optionen</h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Zusätzliche Checkboxen die im Kunden-Signatur-Popup nach QR-Scan erscheinen. Reihenfolge über <b>Order-Index</b> steuerbar.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {templates.length > 0 && (
              <button
                onClick={() => setShowTemplates((v) => !v)}
                data-testid="toggle-templates"
                className="h-10 px-4 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 font-semibold text-[13px] inline-flex items-center gap-1.5 hover:bg-zinc-700"
              >
                <BookMarked size={16} /> Vorlagen ({templates.filter((t) => !t.already_used).length})
              </button>
            )}
            <button
              onClick={() => setEdit({ ...empty })}
              data-testid="add-signing-option"
              className="h-10 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964]"
            >
              <Plus size={16} /> Neue Option
            </button>
          </div>
        </div>

        {/* Iter88zaq — Vorlagen-Bibliothek: Ein-Klick-Übernahme */}
        {showTemplates && templates.length > 0 && (
          <div className="mb-4 bg-[#18181B] border border-zinc-800 rounded-2xl p-4" data-testid="template-library">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="font-bold text-[14px] flex items-center gap-1.5"><BookMarked size={15} className="text-[#00E575]" /> Standard-Vorlagen</h2>
                <p className="text-[11.5px] text-zinc-500 mt-0.5">
                  Ein Klick fügt die Vorlage als neue Option hinzu. Danach kannst du sie individuell anpassen.
                </p>
              </div>
              <button onClick={() => setShowTemplates(false)} className="p-1 hover:bg-zinc-800 rounded text-zinc-400" aria-label="Vorlagen schließen">
                <X size={16} />
              </button>
            </div>
            {(() => {
              const cats = Array.from(new Set(templates.map((t) => t.category || "Sonstige")));
              return cats.map((cat) => (
                <div key={cat} className="mt-3 first:mt-0">
                  <div className="text-[10.5px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{cat}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {templates.filter((t) => (t.category || "Sonstige") === cat).map((tpl) => (
                      <div
                        key={tpl.key}
                        data-testid={`template-card-${tpl.key}`}
                        className={`rounded-xl border p-3 transition ${tpl.already_used
                          ? "border-zinc-800 bg-zinc-900/40 opacity-60"
                          : "border-zinc-800 bg-[#0F0F11] hover:border-[#00E575]/60"}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13px] font-bold text-white truncate">{tpl.title}</div>
                            <div className="text-[11.5px] text-zinc-400 mt-1 line-clamp-2">{tpl.description}</div>
                            <div className="text-[10.5px] text-zinc-500 mt-1.5 italic">☐ {tpl.checkbox_label}</div>
                          </div>
                          {tpl.already_used && (
                            <div className="shrink-0 text-[10.5px] font-bold uppercase tracking-wider text-emerald-400 inline-flex items-center gap-1">
                              <Check size={12} /> Aktiv
                            </div>
                          )}
                        </div>
                        {tpl.requires_check_to_sign && (
                          <div className="mt-2 inline-block text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold uppercase tracking-wider">Pflicht-Option</div>
                        )}
                        {!tpl.already_used && (
                          <div className="mt-3 flex items-center gap-2">
                            <button
                              onClick={() => applyTemplate(tpl)}
                              disabled={tplBusyKey === tpl.key}
                              data-testid={`template-apply-${tpl.key}`}
                              className="flex-1 h-8 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[11.5px] inline-flex items-center justify-center gap-1 hover:bg-[#00C964] disabled:opacity-50"
                            >
                              {tplBusyKey === tpl.key ? "…" : <><Plus size={13} /> Direkt übernehmen</>}
                            </button>
                            <button
                              onClick={() => customizeTemplate(tpl)}
                              data-testid={`template-customize-${tpl.key}`}
                              className="h-8 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 font-semibold text-[11.5px] inline-flex items-center justify-center gap-1 hover:bg-zinc-700"
                              title="Vor dem Speichern anpassen"
                            >
                              <Sliders size={13} /> Anpassen
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        {loading ? (
          <div className="text-zinc-400">Laden…</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-400 bg-[#18181B] border border-zinc-800 rounded-2xl p-6 text-center">
            Noch keine Signatur-Optionen konfiguriert. Klick &bdquo;Neue Option&ldquo; um eine hinzuzufügen.
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((o) => (
              <div key={o.id} data-testid={`signing-option-row-${o.id}`}
                   className={`bg-[#18181B] border rounded-2xl p-4 ${o.enabled ? "border-zinc-800" : "border-zinc-800 opacity-60"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-[14px]">{o.title}</h3>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">#{o.order_index}</span>
                      {o.requires_check_to_sign && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">Pflicht</span>}
                    </div>
                    <p className="text-[12.5px] text-zinc-400 mt-1 whitespace-pre-line">{o.description || "—"}</p>
                    <p className="text-[11.5px] text-zinc-500 mt-1.5 italic">☐ {o.checkbox_label}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggleEnabled(o)} title={o.enabled ? "Deaktivieren" : "Aktivieren"}
                            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400">
                      {o.enabled ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                    <button onClick={() => setEdit({ ...o })} className="p-2 rounded-lg hover:bg-zinc-800 text-sky-300">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(o.id)} className="p-2 rounded-lg hover:bg-zinc-800 text-rose-300">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {edit && (
        <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-lg">{edit.id ? "Option bearbeiten" : edit._from_template ? "Vorlage anpassen" : "Neue Option"}</h2>
              <button onClick={() => setEdit(null)} className="p-1 hover:bg-zinc-800 rounded"><X size={18} /></button>
            </div>
            {edit._from_template && !edit.id && (
              <div className="mb-3 text-[11.5px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 inline-flex items-center gap-1.5" data-testid="from-template-hint">
                <Sliders size={13} />
                Aus Vorlage vorbefüllt — passe Titel und Text nach Bedarf an, klicke dann &bdquo;Speichern&ldquo;
              </div>
            )}
            <div className="space-y-3 text-[13px]">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Titel</label>
                <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  data-testid="opt-title"
                  className="w-full mt-1 bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Beschreibungstext</label>
                <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                  data-testid="opt-description" rows={4}
                  className="w-full mt-1 bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Checkbox-Label</label>
                <input value={edit.checkbox_label} onChange={(e) => setEdit({ ...edit, checkbox_label: e.target.value })}
                  data-testid="opt-checkbox-label"
                  className="w-full mt-1 bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">Reihenfolge</label>
                  <input type="number" value={edit.order_index} onChange={(e) => setEdit({ ...edit, order_index: parseInt(e.target.value) || 0 })}
                    className="w-full mt-1 bg-[#0F0F11] border border-zinc-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div className="flex flex-col justify-end gap-2">
                  <label className="inline-flex items-center gap-2 text-[12px] text-zinc-300">
                    <input type="checkbox" checked={edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} />
                    Aktiv
                  </label>
                  <label className="inline-flex items-center gap-2 text-[12px] text-zinc-300">
                    <input type="checkbox" checked={edit.requires_check_to_sign} onChange={(e) => setEdit({ ...edit, requires_check_to_sign: e.target.checked })} />
                    Haken zum Unterschreiben erforderlich
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEdit(null)} className="h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px]">
                Abbrechen
              </button>
              <button onClick={save} data-testid="opt-save"
                className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964]">
                <Save size={14} /> Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
